import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * Dynamic Entity Collection name for one Organization Entity
 */
export type Name =
  ec.Name.Dynamic<`${typeof Name.PREFIX}${typeof Name.SEPARATOR}${ents.core.dms.models.Entity.Id}`>

export namespace Name {
  export const SEPARATOR = '-'
  export const PREFIX = 'SpaceOrganization'

  /** Returns the dynamic collection name for the given Organization Entity. */
  export function from(entityId: ents.core.dms.models.Entity.Id): Name {
    return ec.Name.Dynamic.from(`${PREFIX}${SEPARATOR}${entityId}`)
  }

  /** Checks whether a collection name belongs to an Organization EC. */
  export const isName: TypeChecker<Name> = TypeChecker.Factory.custom(
    (name: string): name is Name => {
      if (!ec.Name.Dynamic.isDynamic(name)) {
        return false
      }
      const parts = name.slice(ec.Name.Dynamic.PREFIX.length).split(SEPARATOR)
      if (parts.length !== 2) {
        return false
      }
      const [prefix, entityId] = parts
      return prefix === PREFIX && ents.core.dms.models.Entity.Id.isId(entityId)
    },
    { typeDescription: 'SpaceOrganizationName' },
  )

  export type All = `${Name}${typeof SEPARATOR}${typeof All.SUFFIX}`
  export namespace All {
    export const SUFFIX = 'All'

    /**
     * Returns the dynamic collection name for the "all" Organization EC for the given Organization Entity
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from(entityId: ents.core.dms.models.Entity.Id): All {
      return `${Name.from(entityId)}${SEPARATOR}${SUFFIX}`
    }

    /**
     * Checks whether a collection name belongs to an "all" Organization EC.
     */
    export const isAll: TypeChecker<All> = TypeChecker.Factory.custom(
      (name: string): name is All => {
        if (!ec.Name.Dynamic.isDynamic(name)) {
          return false
        }
        const parts = name.slice(ec.Name.Dynamic.PREFIX.length).split(SEPARATOR)
        if (parts.length !== 3) {
          return false
        }
        const [prefix, entityId, suffix] = parts
        return (
          prefix === PREFIX &&
          ents.core.dms.models.Entity.Id.isId(entityId) &&
          suffix === SUFFIX
        )
      },
      { typeDescription: 'SpaceOrganizationNameAll' },
    )
  }
}
